import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DialogRef } from '@ngneat/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { fromEvent, Subscription } from 'rxjs';
import { OFFLINE_MODAL_RECONNECT_CLOSE_REASON } from '@app/core/actions/environment.actions';
import { SmashMessageModalComponent } from '../smash-message-modal/smash-message-modal.component';

/**
 * Shown when a lazy-route chunk fails to load (sustained connection loss, or a stale bundle after a
 * deploy whose chunk hashes were purged). Built on the generic SmashMessageModalComponent shell.
 * "Reload" is user-initiated (never automatic — a reload would kill an in-progress upload) and does
 * a full document reload, which is the only thing that recovers a stale deploy (fresh index.html ->
 * new chunk hashes). The literal CHUNK_LOAD_ERROR_* keys live here so ngx-translate-extract sees
 * them. Opened by a browser-only effect on ChunkLoadFailure.
 *
 * Auto-closes when the browser regains connectivity: the modal is a blocking dialog, so once the
 * failed navigation left it open, coming back online would otherwise leave the user STUCK behind a
 * "connection lost" overlay (unable to reach the language selector or anything else) even though the
 * network is back. On the `online` event we close it, restoring a normal UI the user can act on.
 */
@Component({
    selector: 'app-modal-chunk-load-error',
    standalone: true,
    imports: [SmashMessageModalComponent, TranslateModule],
    templateUrl: './modal-chunk-load-error.component.html',
})
export class ModalChunkLoadErrorComponent implements OnInit, OnDestroy {
    private onlineSubscription?: Subscription;

    constructor(
        public ref: DialogRef,
        @Inject(DOCUMENT) private document: Document,
    ) { }

    ngOnInit(): void {
        const view = this.document.defaultView;
        if (view) {
            // Close with the 'online' reason so the opening effect knows this was the reconnect
            // auto-close (which completes the switch), NOT a user dismissal (which cancels it).
            this.onlineSubscription = fromEvent(view, 'online').subscribe(() => this.ref.close(OFFLINE_MODAL_RECONNECT_CLOSE_REASON));
        }
    }

    ngOnDestroy(): void {
        this.onlineSubscription?.unsubscribe();
    }

    reload(): void {
        this.document.location.reload();
    }
}
